import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

const servEnvSchema = {
  CLERK_SECRET_KEY: z.string(),
  CLERK_WEBHOOK_SECRET: z.string(),
  DB_URL: z.string(),
  DB_SSL_REJECT_UNAUTHORIZED: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  EXTERNAL_ACCESS_LINKS_SECRET: z.string().min(1),
  // Mints wallboard enrolment codes. Optional so the dashboard boots where no wallboard exists;
  // the enrol procedure fails loudly rather than falling back to another secret.
  WALLBOARD_ENROLMENT_SECRET: z.string().min(32).optional(),
  // NL filter. `AI_PROVIDER` names the provider outright, so a second key lying around
  // can never route spend. Keys stay optional: with none the procedure answers `no_key`.
  AI_PROVIDER: z.enum(['anthropic', 'openrouter']).default('openrouter'),
  ANTHROPIC_API_KEY: z.string().min(1).optional(),
  OPENROUTER_API_KEY: z.string().min(1).optional(),
  SVIX_AUTH_TOKEN: z.string(),
  RESEND_SVIX_SIGNATURE_SECRET: z.string(),
  EMAIL_JOBS_QUEUE_URL: z.string().optional(),
  EMAIL_JOBS_ROLE_ARN: z.string().optional(),
  // Hex key behind every engraved serial (`openssl rand -hex 32`). Checked when serials are
  // allocated, not here: a bad paste must not take the dashboard down.
  SERIAL_NUMBER_KEY: z.string().optional(),
  VERCEL_ENV: z.enum(['production', 'preview', 'development']).optional(),
  GOOGLE_MAPS_API_KEY: z.string(),
  ABLY_API_KEY: z.string(),
  // Separate key so `revocableTokens` (and its 1h TTL cap) applies to share links only. Never
  // fall back to ABLY_API_KEY: tokens from a non-revocable key can never be revoked.
  ABLY_EXTERNAL_API_KEY: z.string().optional(),
  // Vercel OIDC role behind /admin/fleet. Unset, only local dev reaches AWS, via FLEET_AWS_PROFILE.
  FLEET_ROLE_ARN: z.string().optional(),
  FLEET_AWS_PROFILE: z.string().optional(),
  // Vercel OIDC role allowed iot:Publish on device/*/cmd. Unset, sending a device command answers 503.
  DEVICE_CONTROL_ROLE_ARN: z.string().optional(),
  COMFYCOM_BASE_URL: z.string().optional(),
  COMFYCOM_API_TOKEN: z.string().optional(),
  // Path secret in the URL we give Comfycom for pushes. Unset, the endpoint answers 404.
  COMFYCOM_PUSH_TOKEN: z.string().min(32).optional(),
  // Slack incoming webhook that echoes each push body (#product-monitoring). Unset, it is a no-op.
  COMFYCOM_PUSH_SLACK_WEBHOOK_URL: z.url().optional(),
  FIRMWARE_BUCKET: z.string().optional(),
  IOT_DATA_ENDPOINT: z.string().optional(),
  AWS_ACCOUNT_ID: z.string().optional(),
  AWS_REGION: z.string().default('eu-central-1'),
  // Installation-media (evidence photos). Optional until the S3/OIDC infra is enabled per env —
  // required would break boot everywhere the bucket doesn't exist yet. Make both required and drop
  // the runtime guard in lib/installation-media.ts once every env has values.
  INSTALLATION_MEDIA_BUCKET: z.string().optional(),
  INSTALLATION_MEDIA_ROLE_ARN: z.string().optional(),
  // LocalStack only — set, it switches the S3 client to path-style against the local endpoint.
  AWS_ENDPOINT_URL: z.string().optional(),
  // Where AI spans are posted. Not NEXT_PUBLIC_POSTHOG_HOST: that is the UI host, and
  // spans posted there are dropped.
  POSTHOG_INGEST_HOST: z.string().url().default('https://eu.i.posthog.com'),
};

const clientEnvSchema = {
  NEXT_PUBLIC_WEB_BASE_URL: z.string(),
  NEXT_PUBLIC_INSTALLER_BASE_URL: z.string(),
  NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: z.string(),
  NEXT_PUBLIC_MAPTILER_API_KEY: z.string(),
  // PostHog (error tracking + session replay). Optional so dev/preview without
  // PostHog configured still boots; init is gated on the key being present.
  NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN: z.string().optional(),
  NEXT_PUBLIC_POSTHOG_HOST: z.string().default('https://eu.posthog.com'),
  // Set to 'true' in .env to initialise PostHog in local `next dev` (off by
  // default so dev doesn't spam the project). Also enables verbose SDK logging.
  NEXT_PUBLIC_POSTHOG_DEBUG: z.enum(['true', 'false']).optional(),
  // Deploy SHA (mapped from VERCEL_GIT_COMMIT_SHA in next.config.mjs) — PostHog
  // `appVersion` super property for release correlation. 'dev' locally.
  NEXT_PUBLIC_GIT_COMMIT_SHA: z.string().optional(),
};

export const envSchema = z.intersection(z.object(servEnvSchema), z.object(clientEnvSchema));

export const env = createEnv({
  server: servEnvSchema,
  client: clientEnvSchema,
  shared: {
    NODE_ENV: z.enum(['development', 'production', 'test']).default('development'),
    NGROK_URL: z.string().optional(),
  },
  experimental__runtimeEnv: {
    NODE_ENV: process.env.NODE_ENV,
    NGROK_URL: process.env.NGROK_URL,
    NEXT_PUBLIC_WEB_BASE_URL: process.env.NEXT_PUBLIC_WEB_BASE_URL,
    NEXT_PUBLIC_INSTALLER_BASE_URL: process.env.NEXT_PUBLIC_INSTALLER_BASE_URL,
    NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY,
    NEXT_PUBLIC_MAPTILER_API_KEY: process.env.NEXT_PUBLIC_MAPTILER_API_KEY,
    NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN: process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN,
    NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    NEXT_PUBLIC_POSTHOG_DEBUG: process.env.NEXT_PUBLIC_POSTHOG_DEBUG,
    NEXT_PUBLIC_GIT_COMMIT_SHA: process.env.NEXT_PUBLIC_GIT_COMMIT_SHA,
  },
});
